import { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Button, InputBoxSingle } from '@peacecover/component';
import { useUpdateCastRelationship } from '../../../domains/scene/ui/hooks/useUpdateCastRelationship';

interface SettingThirdPersonaPopupProps {
  castRelationshipId: string;
  onClose: () => void;
}

const SettingThirdPersonaPopup = ({
  castRelationshipId,
  onClose
}: SettingThirdPersonaPopupProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [thirdPersonaName, setThirdPersonaName] = useState('');
  const { mutateAsync: updateCastRelationship } = useUpdateCastRelationship();

  const handleUpdateCastRelationship = async () => {
    if (!thirdPersonaName.trim()) return;
    try {
      const requestBody = {
        cast_relationship: {
          person_c: {
            name: thirdPersonaName
          }
        }
      };

      const response = await updateCastRelationship({
        castRelationshipId: castRelationshipId,
        body: requestBody
      });

      if (response?.castRelationshipId) {
        queryClient.invalidateQueries(['sceneDetail']);
        queryClient.invalidateQueries(['sceneContents']);
        onClose();
      }
    } catch (error) {
      console.error(error);
    }
  };

  return (
    // biome-ignore lint/a11y/useSemanticElements: 내부에 <button>들이 있어 최상위 컨테이너를 <button>으로 바꾸면 중첩 버튼이 되어 HTML/a11y가 더 악화됩니다.
    <div
      role="button"
      tabIndex={0}
      onClick={onClose}
      className="fixed top-0 left-0 w-full h-full bg-black/50 z-[1000] flex items-center justify-center"
    >
      <div
        role="dialog"
        aria-modal="true"
        className="bg-backgroundElevated-dark rounded-[24px] px-[14px] pt-[32px] pb-[16px] w-[311px] flex flex-col gap-[16px] items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col gap-[14px] items-center">
          <p className="text-[17px] font-bold leading-[20px] text-main-dark-1">
            이 씬에서는 새로운 등장인물이 있어요
          </p>
          <p className="text-[13px] leading-[16px] font-medium text-main-dark-2">
            새로 등장할 인물의 이름을 입력해주세요
          </p>
        </div>
        <div className="setting-third-persona-input-wrapper w-full">
          <InputBoxSingle
            value={thirdPersonaName}
            onChange={(e) => setThirdPersonaName(e.target.value)}
            placeholder={t('SceneSetting.enterUserInfo.placeholder') as string}
            className="w-full bg-transparent text-[17px] font-semibold !text-main-dark-2 placeholder:text-placeholder-dark"
            showIcon={thirdPersonaName.trim().length > 0}
            onClear={() => setThirdPersonaName('')}
          />
        </div>
        <div className="flex flex-col px-[8px] w-full">
          <Button
            size="large"
            disabled={!thirdPersonaName.trim()}
            className="w-full text-pure-white bg-primary-light font-semibold disabled:bg-[#303030] disabled:text-[#4D4D50] text-[17px]"
            onClick={handleUpdateCastRelationship}
          >
            {t('done')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SettingThirdPersonaPopup;
